import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { ProductCard } from "@/components/shop/ProductCard";

export const brandSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const NICE = { MF: "Massey Ferguson (MF)" };

const BrandIndex = ({ fits }) => {
  const { t } = useLang();
  useSeo({ title: t("bp_index_title"), description: t("bp_index_desc") });
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <h1 data-testid="brand-index-title" className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight">{t("bp_index_title")}</h1>
      <p className="text-slate-400 mt-4 max-w-2xl">{t("bp_index_desc")}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-10">
        {fits.map((b) => (
          <Link key={b} to={`/traktoriu-sedynes/${brandSlug(b)}`} data-testid={`brand-link-${brandSlug(b)}`}
            className="flex items-center justify-between border border-[#3B4157] bg-[#292D3C] px-5 h-16 hover:border-orange-500 hover:text-orange-500 transition-colors">
            <span className="font-display font-bold uppercase tracking-wide">{NICE[b] || b} {t("bp_seats")}</span><ChevronRight className="w-4 h-4" />
          </Link>
        ))}
      </div>
    </div>
  );
};

const BrandPage = ({ brand }) => {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const name = NICE[brand] || brand;
  useSeo({ title: `${name} ${t("bp_title_suffix")}`, description: t("bp_desc").replace("{b}", name) });
  useEffect(() => { api.get("/products", { params: { fits: brand } }).then((r) => setItems(r.data)); }, [brand]);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <nav className="text-xs font-mono-x text-slate-500 mb-6"><Link to="/traktoriu-sedynes" className="hover:text-orange-500">{t("bp_index_title")}</Link> / {name}</nav>
      <h1 data-testid="brand-page-title" className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight">{name} {t("bp_title_suffix")}</h1>
      <p data-testid="brand-page-intro" className="text-slate-400 mt-4 max-w-3xl">{t("bp_desc").replace("{b}", name)}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {items?.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
      </div>
      {items && !items.length && <p className="text-slate-400 py-16 text-center">{t("no_results")}</p>}
      <div className="mt-12 border-t border-[#3B4157] pt-8 text-sm text-slate-400 max-w-3xl">
        {t("bp_more")} <Link to="/seat-finder" className="text-orange-500 underline underline-offset-2">{t("nav_finder")}</Link>
      </div>
    </div>
  );
};

export default function BrandSeats() {
  const { slug } = useParams();
  const [fits, setFits] = useState(null);
  useEffect(() => { api.get("/products/fits").then((r) => setFits(r.data)); }, []);
  if (!fits) return <div className="min-h-[50vh]" />;
  if (!slug) return <BrandIndex fits={fits} />;
  const brand = fits.find((b) => brandSlug(b) === slug);
  return brand ? <BrandPage brand={brand} /> : <BrandIndex fits={fits} />;
}
